import {
  streamStopped,
  streamStarting,
  streamingActive,
  getStoppedReason,
  streamPending,
  getBroadcastTimeRange
} from 'hooks/useStreamButtonState/utils';
import { getSecondsTo, getSecondsFrom } from '@shoflo/polymer';
import {
  STREAM_ALERT_PERSISTENT,
  STREAM_ALERT_EPHEMERAL
} from '@shoflo/studio-redux/constants/StreamAlertTypes';
import { STOPPED_BY_USER } from '@shoflo/studio-redux/constants/StreamStoppedReasons';
import { createStreamAlert } from '@shoflo/studio-redux/utils';
import moment from 'moment';

const twentyFourHours = 86400;
const oneHour = 60 * 60;
const oneMinute = 60;
const fiveMinutes = 60 * 5;
const nineMinutes = 60 * 9;
const tenMinutes = 60 * 10;

/**
 * @description Host StreamAlertGetter
 * This class has a method: _createAlerts() that takes in broadcast information
 * and uses conditions to return an array of StreamAlerts that match conditions
 * Logic conditions: https://wiki.cvent.com/display/STUDIO/Studio+Alerts%2C+Stream+Button%2C+Countdown+Timer+States
 */
export default class StreamAlerts {
  /**
   *
   * @param broadcast
   * @param endpoints
   * @param serverTimeDelta
   * @param flashStarted
   * @param timedOut
   */
  constructor(broadcast, endpoints, serverTimeDelta, flashStarted, timedOut) {
    this._timedOut = timedOut;
    this._flashStarted = flashStarted;
    this._broadcast = broadcast;
    this._endpoints = endpoints;
    this._serverTimeDelta = serverTimeDelta;
    this._isStreamPending = streamPending(this._endpoints);
    this._isStreamStarting = streamStarting(this._endpoints);
    this._hasStreamStarted = streamingActive(this._endpoints);
    this._hasStreamStopped = streamStopped(this._endpoints);
    this._timeInSecondsSinceStopped = getSecondsFrom(
      this._endpoints[0]?.endedAt ?? 0
    );

    this._wasStreamStoppedBeforeStartTime = false;
    if (this._hasStreamStopped && this._endpoints[0]?.endedAt) {
      this._wasStreamStoppedBeforeStartTime = moment(
        this._endpoints[0]?.endedAt
      ).isBefore(broadcast.startOn);
    }

    this._stoppedReason = getStoppedReason(this._endpoints);
    this._broadcastStartMinus5 = broadcast.startOn - 60 * 5 * 1000;
    this._broadcastEndMinus1 = broadcast.endOn - 60 * 1000;
    this._broadcastEndMinus5 = broadcast.endOn - 60 * 5 * 1000;
    const { serverTimeToStart, serverTimeToEnd } = getBroadcastTimeRange({
      serverTimeDelta: this._serverTimeDelta,
      broadcast: this._broadcast
    });
    this._serverTimeToStart = serverTimeToStart;
    this._serverTimeToEnd = serverTimeToEnd;
    this.alerts = [];
    this._createAlerts();
  }

  /**
   *
   * @private
   */
  _createAlerts() {
    if (this._endpoints.length < 1) {
      return;
    }

    const immediateAlert = this._getImmediateAlert();
    if (immediateAlert !== null) {
      this.alerts.push(immediateAlert);
    }
    this.alerts.push(...this._getDelayedAlerts());
  }

  /**
   * These alerts are to be fired immediately.
   * @returns {null|{color: string, delayInSeconds: any | number, options: {type: string, indefinite: boolean, timeout}, message: string}}
   * @private
   */
  _getImmediateAlert() {
    if (
      this._serverTimeToStart <= twentyFourHours &&
      this._serverTimeToStart > fiveMinutes &&
      !this._hasStreamStarted &&
      !this._isStreamStarting
    ) {
      return createStreamAlert(
        'info',
        'You aren’t live streaming to attendees. This is a good time to prepare with speakers.',
        STREAM_ALERT_PERSISTENT
      );
    }

    if (this._timedOut) {
      const message = this._isStreamStarting
        ? 'Starting the stream is taking longer than expected. Please cancel and try again.'
        : 'Stopping the stream is taking longer than expected. Please force stop.';

      return createStreamAlert('alert', message, STREAM_ALERT_PERSISTENT);
    }

    if (this._isStreamStarting) {
      return createStreamAlert(
        'alert',
        'Hang tight while we start the live stream.',
        STREAM_ALERT_PERSISTENT
      );
    }

    if (
      this._hasStreamStopped &&
      this._stoppedReason === STOPPED_BY_USER &&
      this._serverTimeToEnd < 0 &&
      this._timeInSecondsSinceStopped < oneHour
    ) {
      return createStreamAlert(
        'info',
        'Live stream stopped. It’s just you and the speakers now.',
        STREAM_ALERT_PERSISTENT
      );
    }

    if (this._hasStreamStopped && this._stoppedReason !== STOPPED_BY_USER) {
      return createStreamAlert(
        'alert',
        'The stream has been interrupted. Please restart the stream to reach your audience.',
        STREAM_ALERT_PERSISTENT
      );
    }

    if (this._flashStarted) {
      return createStreamAlert(
        'success',
        'You’re now live streaming to attendees!',
        STREAM_ALERT_EPHEMERAL
      );
    }

    return null;
  }

  /**
   * These alerts are the one that are delayed and to be queued
   * @returns {*[]}
   * @private
   */
  _getDelayedAlerts() {
    const delayedAlerts = [];

    if (
      this._serverTimeToStart > 0 &&
      this._serverTimeToStart < twentyFourHours &&
      (this._isStreamPending || this._hasStreamStopped)
    ) {
      delayedAlerts.push(
        createStreamAlert(
          'alert',
          'Attendees are joining. The presentation begins when you start streaming.',
          STREAM_ALERT_PERSISTENT,
          true,
          getSecondsTo(this._broadcastStartMinus5)
        )
      );
    }

    if (
      this._serverTimeToStart < twentyFourHours &&
      this._serverTimeToEnd > 0 &&
      (this._isStreamPending || this._wasStreamStoppedBeforeStartTime)
    ) {
      // queue this up to fire on start time, and would stay until it is no longer valid
      // ie when end of broadcast or stream is no longer pending
      delayedAlerts.push(
        createStreamAlert(
          'warning',
          'Attendees are waiting for you to start the live stream.',
          STREAM_ALERT_PERSISTENT,
          false,
          getSecondsTo(this._broadcast.startOn)
        )
      );
    }

    if (
      this._hasStreamStopped &&
      this._stoppedReason === STOPPED_BY_USER &&
      this._timeInSecondsSinceStopped < fiveMinutes &&
      !this._wasStreamStoppedBeforeStartTime
    ) {
      // queue this up to fire on 5 minutes after time stopped
      delayedAlerts.push(
        createStreamAlert(
          'warning',
          'Heads up! The recording will begin processing in 5 minutes. You will be unable to restart the stream.',
          STREAM_ALERT_EPHEMERAL,
          true,
          fiveMinutes - this._timeInSecondsSinceStopped
        )
      );
    }

    if (
      this._hasStreamStopped &&
      this._stoppedReason === STOPPED_BY_USER &&
      this._timeInSecondsSinceStopped < tenMinutes &&
      !this._wasStreamStoppedBeforeStartTime
    ) {
      delayedAlerts.push(
        createStreamAlert(
          'warning',
          'Heads up! The recording will begin processing soon. You will be unable to restart the stream.',
          STREAM_ALERT_EPHEMERAL,
          true,
          nineMinutes - this._timeInSecondsSinceStopped,
          oneMinute
        )
      );
    }

    return delayedAlerts;
  }
}
